import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { API_ENDPOINTS } from '../config/api';

const LearnPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const student = location.state?.student;

  // State management
  const [subjects, setSubjects] = useState([]);
  const [topics, setTopics] = useState([]);
  const [lessons, setLessons] = useState([]);
  const [loading, setLoading] = useState(true);

  // Navigation state
  const [selectedSubject, setSelectedSubject] = useState(null);
  const [selectedTopic, setSelectedTopic] = useState(null);
  const [selectedLesson, setSelectedLesson] = useState(null);
  const [expandedSubjects, setExpandedSubjects] = useState(new Set());

  // Progress tracking
  const [progress, setProgress] = useState([]);
  const [completedLessons, setCompletedLessons] = useState(new Set());
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!student) {
      navigate('/student-login');
      return;
    }
    loadLearningData();
  }, [student, navigate]);

  const loadLearningData = async () => {
    try {
      setLoading(true);

      // Load all data for the student's class
      const [subjectsRes, topicsRes, lessonsRes, progressRes] = await Promise.all([
        fetch(`${API_ENDPOINTS.SUBJECTS}`),
        fetch(`${API_ENDPOINTS.BASE_URL}/api/topics`),
        fetch(`${API_ENDPOINTS.LESSONS}`),
        fetch(`${API_ENDPOINTS.BASE_URL}/api/students/progress/${student.username}`)
      ]);

      const subjectsData = await subjectsRes.json();
      const topicsData = await topicsRes.json();
      const lessonsData = await lessonsRes.json();
      const progressData = await progressRes.json();

      // Filter data for student's class (case-insensitive)
      const classSubjects = subjectsData.filter(subject =>
        subject.className?.toLowerCase() === student.className?.toLowerCase()
      );

      const classTopics = topicsData.filter(topic =>
        topic.className?.toLowerCase() === student.className?.toLowerCase()
      );

      const classLessons = lessonsData.filter(lesson =>
        lesson.className?.toLowerCase() === student.className?.toLowerCase()
      );

      // Ensure all subjects have at least some topics, even if empty
      const subjectsWithTopics = classSubjects.map(subject => {
        const subjectTopics = classTopics.filter(topic => topic.subjectId === subject.id);
        return {
          ...subject,
          topics: subjectTopics,
          topicCount: subjectTopics.length
        };
      });

      setSubjects(subjectsWithTopics);
      setTopics(classTopics);
      setLessons(classLessons);
      setProgress(progressData);

      // Initialize completed lessons
      const completed = new Set(progressData.filter(p => p.completed).map(p => p.lessonId || p.id));
      setCompletedLessons(completed);

      console.log('Loaded data:', {
        subjects: subjectsWithTopics.length,
        topics: classTopics.length,
        lessons: classLessons.length,
        progress: progressData.length
      });

    } catch (error) {
      console.error('Error loading learning data:', error);
      setError('Failed to load learning materials. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const toggleSubjectExpansion = (subjectId) => {
    const newExpanded = new Set(expandedSubjects);
    if (newExpanded.has(subjectId)) {
      newExpanded.delete(subjectId);
    } else {
      newExpanded.add(subjectId);
    }
    setExpandedSubjects(newExpanded);
  };

  const selectSubject = (subject) => {
    setSelectedSubject(subject);
    setSelectedTopic(null);
    setSelectedLesson(null);
  };

  const selectTopic = (topic) => {
    setSelectedTopic(topic);
    setSelectedLesson(null);
  };

  const selectLesson = (lesson) => {
    setSelectedLesson(lesson);
    // Mark lesson as started (in progress)
    updateLessonProgress(lesson, false);
  };

  const updateLessonProgress = async (lesson, completed) => {
    try {
      const progressData = {
        username: student.username,
        lessonId: lesson.id,
        subject: lesson.subjectName,
        topic: topics.find(t => t.id === lesson.topicId)?.name || 'Unknown Topic',
        score: completed ? 100 : 0, // Simple scoring for now
        completed: completed,
        notes: completed ? 'Lesson completed' : 'Lesson started'
      };

      await fetch(`${API_ENDPOINTS.BASE_URL}/api/students/progress`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(progressData)
      });

      if (completed) {
        setCompletedLessons(prev => new Set([...prev, lesson.id]));
      }

      // Refresh progress data
      const progressRes = await fetch(`${API_ENDPOINTS.BASE_URL}/api/students/progress/${student.username}`);
      const progressDataFresh = await progressRes.json();
      setProgress(progressDataFresh);

    } catch (error) {
      console.error('Error updating progress:', error);
    }
  };

  const markLessonCompleted = () => {
    if (selectedLesson) {
      updateLessonProgress(selectedLesson, true);
    }
  };

  const getTopicsForSubject = (subjectId) => {
    return topics.filter(topic => topic.subjectId === subjectId);
  };

  const getLessonsForTopic = (topicId) => {
    return lessons.filter(lesson => lesson.topicId === topicId);
  };

  const getSubjectProgress = (subjectId) => {
    const subjectLessons = lessons.filter(lesson =>
      topics.some(topic => topic.subjectId === subjectId && topic.id === lesson.topicId)
    );
    const completedCount = subjectLessons.filter(lesson => completedLessons.has(lesson.id)).length;
    return subjectLessons.length > 0 ? Math.round((completedCount / subjectLessons.length) * 100) : 0;
  };

  const getTopicProgress = (topicId) => {
    const topicLessons = getLessonsForTopic(topicId);
    const completedCount = topicLessons.filter(lesson => completedLessons.has(lesson.id)).length;
    return topicLessons.length > 0 ? Math.round((completedCount / topicLessons.length) * 100) : 0;
  };

  if (!student) return null;

  if (loading) {
    return (
      <div className="min-h-screen bg-blue-50 flex items-center justify-center">
        <div className="text-xl text-blue-600">Loading your learning materials...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-blue-50 flex items-center justify-center">
        <div className="max-w-md mx-auto text-center p-8 bg-white rounded-lg shadow">
          <div className="text-6xl mb-4">Warning</div>
          <h2 className="text-xl font-bold text-gray-900 mb-4">Learning Materials Error</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={() => {
              setError(null);
              loadLearningData();
            }}
            className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-blue-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Learn</h1>
              <p className="text-sm text-gray-600">Access your lessons and study materials</p>
            </div>
            <button
            onClick={() => navigate('/student-dashboard', { state: { student } })}
              className="bg-gray-600 text-white px-4 py-2 rounded hover:bg-gray-700"
            >
              Back to Dashboard
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Sidebar Navigation */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold text-gray-800 mb-4">Course Materials</h2>

              {subjects.length === 0 ? (
                <p className="text-gray-500 text-sm">No subjects available for your class.</p>
              ) : (
                <div className="space-y-2">
                  {subjects.map((subject) => (
                    <div key={subject.id}>
                      {/* Subject Header */}
                      <div
                        className={`flex items-center justify-between p-3 rounded cursor-pointer transition-colors ${
                          selectedSubject?.id === subject.id
                            ? 'bg-blue-100 text-blue-800'
                            : 'hover:bg-gray-100'
                        }`}
                        onClick={() => selectSubject(subject)}
                      >
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleSubjectExpansion(subject.id);
                            }}
                            className="text-gray-500 hover:text-gray-700"
                          >
                            {expandedSubjects.has(subject.id) ? '▼' : '▶'}
                          </button>
                          <span className="font-medium">{subject.name}</span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <span className="text-xs text-gray-500">
                            {getSubjectProgress(subject.id)}%
                          </span>
                          <div className="w-12 bg-gray-200 rounded-full h-2">
                            <div
                              className="bg-blue-600 h-2 rounded-full"
                              style={{ width: `${getSubjectProgress(subject.id)}%` }}
                            ></div>
                          </div>
                        </div>
                      </div>

                      {/* Topics */}
                      {expandedSubjects.has(subject.id) && (
                        <div className="ml-6 mt-2 space-y-1">
                          {getTopicsForSubject(subject.id).map((topic) => (
                            <div key={topic.id}>
                              <div
                                className={`flex items-center justify-between p-2 rounded cursor-pointer text-sm transition-colors ${
                                  selectedTopic?.id === topic.id
                                    ? 'bg-blue-50 text-blue-800'
                                    : 'hover:bg-gray-50'
                                }`}
                                onClick={() => selectTopic(topic)}
                              >
                                <span>{topic.name}</span>
                                <div className="flex items-center space-x-2">
                                  <span className="text-xs text-gray-500">
                                    {getTopicProgress(topic.id)}%
                                  </span>
                                  <div className="w-8 bg-gray-200 rounded-full h-1">
                                    <div
                                      className="bg-green-600 h-1 rounded-full"
                                      style={{ width: `${getTopicProgress(topic.id)}%` }}
                                    ></div>
                                  </div>
                                </div>
                              </div>

                              {/* Lessons */}
                              {selectedTopic?.id === topic.id && (
                                <div className="ml-4 mt-2 space-y-1">
                                  {getLessonsForTopic(topic.id).map((lesson) => (
                                    <div
                                      key={lesson.id}
                                      className={`flex items-center space-x-2 p-2 rounded cursor-pointer text-xs transition-colors ${
                                        selectedLesson?.id === lesson.id
                                          ? 'bg-blue-50 text-blue-800'
                                          : 'hover:bg-gray-50'
                                      }`}
                                      onClick={() => selectLesson(lesson)}
                                    >
                                      <div className={`w-2 h-2 rounded-full ${
                                        completedLessons.has(lesson.id)
                                          ? 'bg-green-500'
                                          : 'bg-gray-300'
                                      }`}></div>
                                      <span className="truncate">{lesson.title}</span>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Main Content Area */}
          <div className="lg:col-span-3">
            {selectedLesson ? (
              <div className="bg-white rounded-lg shadow">
                {/* Lesson Header */}
                <div className="border-b p-6">
                  <div className="flex justify-between items-start">
                    <div>
                      <h2 className="text-2xl font-bold text-gray-900">{selectedLesson.title}</h2>
                      <p className="text-gray-600 mt-1">
                        {selectedLesson.subjectName} ? {topics.find(t => t.id === selectedLesson.topicId)?.name}
                      </p>
                    </div>
                    <div className="flex items-center space-x-3">
                      <div className={`px-3 py-1 rounded-full text-sm ${
                        completedLessons.has(selectedLesson.id)
                          ? 'bg-green-100 text-green-800'
                          : 'bg-yellow-100 text-yellow-800'
                      }`}>
                        {completedLessons.has(selectedLesson.id) ? '? Completed' : 'Study In Progress'}
                      </div>
                      {!completedLessons.has(selectedLesson.id) && (
                        <button
                          onClick={markLessonCompleted}
                          className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 text-sm"
                        >
                          Mark as Completed
                        </button>
                      )}
                    </div>
                  </div>
                </div>

                {/* Lesson Content */}
                <div className="p-6">
                  <div className="prose max-w-none">
                    <div
                      className="text-gray-800 leading-relaxed"
                      dangerouslySetInnerHTML={{ __html: selectedLesson.content }}
                    />
                  </div>
                </div>

                {/* Lesson Actions */}
                <div className="border-t p-6">
                  <div className="flex justify-between items-center">
                    <div className="text-sm text-gray-600">
                      Lesson {lessons.findIndex(l => l.id === selectedLesson.id) + 1} of {lessons.length}
                    </div>
                    <div className="flex space-x-3">
                      <button className="text-blue-600 hover:text-blue-800 text-sm">
                        Note Add Note
                      </button>
                      <button className="text-green-600 hover:text-green-800 text-sm">
                        Bookmark Bookmark
                      </button>
                      <button className="text-purple-600 hover:text-purple-800 text-sm">
                        Share Share
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="bg-white rounded-lg shadow p-12 text-center">
                <div className="text-6xl mb-4">Books</div>
                <h3 className="text-2xl font-semibold text-gray-800 mb-4">Welcome to Your Learning Hub</h3>
                <p className="text-gray-600 mb-6">
                  Select a subject from the sidebar to start exploring your lessons and study materials.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8">
                  <div className="p-4 border rounded-lg">
                    <div className="text-2xl mb-2">Study</div>
                    <h4 className="font-semibold text-gray-800">Interactive Lessons</h4>
                    <p className="text-sm text-gray-600">Engage with rich, structured content</p>
                  </div>
                  <div className="p-4 border rounded-lg">
                    <div className="text-2xl mb-2">Stats</div>
                    <h4 className="font-semibold text-gray-800">Track Progress</h4>
                    <p className="text-sm text-gray-600">Monitor your learning journey</p>
                  </div>
                  <div className="p-4 border rounded-lg">
                    <div className="text-2xl mb-2">Goals</div>
                    <h4 className="font-semibold text-gray-800">Personalized Learning</h4>
                    <p className="text-sm text-gray-600">Learn at your own pace</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LearnPage;






